import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Globe,
  Layout,
  MousePointerClick,
  FileText,
  Zap,
  Lightbulb,
  CheckCircle2,
  Target,
  BarChart3,
  AlertTriangle,
  BookOpen,
  Video,
  Share2,
  Sparkles,
} from 'lucide-react';

interface AritziaStrategieMarketingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Section =
  | 'overview'
  | 'ux'
  | 'conversion'
  | 'contenu'
  | 'technique'
  | 'reseaux'
  | 'lecons';

const TABS: { id: Section; icon: typeof Globe; label: string }[] = [
  { id: 'overview', icon: Globe, label: 'Vue d\'ensemble' },
  { id: 'ux', icon: Layout, label: 'UX & Navigation' },
  { id: 'conversion', icon: MousePointerClick, label: 'Conversion & CTA' },
  { id: 'contenu', icon: FileText, label: 'Contenu & Storytelling' },
  { id: 'technique', icon: Zap, label: 'Technique & Perf.' },
  { id: 'reseaux', icon: Share2, label: 'Réseaux sociaux & Vidéo' },
  { id: 'lecons', icon: Lightbulb, label: 'Leçons pour BZ' },
];

export default function AritziaStrategieMarketingModal({
  isOpen,
  onClose,
}: AritziaStrategieMarketingModalProps) {
  const [activeSection, setActiveSection] = useState<Section>('overview');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 text-white p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold mb-2 flex items-center gap-3">
                  <Globe className="w-7 h-7" />
                  Stratégie marketing site web — Exemple Aritzia
                </h2>
                <p className="text-white/90 text-sm">
                  Objectif : former l'équipe vente et marketing au marketing web complet — site, IA, vidéo virale, contenu réseaux sociaux. Aritzia comme référence ; stock pour LEX.
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-6">
              {TABS.map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  onClick={() => setActiveSection(id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                    activeSection === id
                      ? 'bg-white text-slate-800 shadow-lg'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            <AnimatePresence mode="wait">
              {activeSection === 'overview' && <OverviewSection key="overview" />}
              {activeSection === 'ux' && <UXSection key="ux" />}
              {activeSection === 'conversion' && <ConversionSection key="conversion" />}
              {activeSection === 'contenu' && <ContenuSection key="contenu" />}
              {activeSection === 'technique' && <TechniqueSection key="technique" />}
              {activeSection === 'reseaux' && <ReseauxVideoSection key="reseaux" />}
              {activeSection === 'lecons' && <LeconsSection key="lecons" />}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ========== Vue d'ensemble (enrichie) ==========
function OverviewSection() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-slate-100 rounded-xl p-5 text-center">
          <Target className="w-10 h-10 mx-auto mb-2 text-slate-600" />
          <p className="text-2xl font-bold text-slate-800">~1,5 G$ CA</p>
          <p className="text-sm text-slate-600">Revenus annuels (ex. 2022)</p>
        </div>
        <div className="bg-slate-100 rounded-xl p-5 text-center">
          <BarChart3 className="w-10 h-10 mx-auto mb-2 text-slate-600" />
          <p className="text-2xl font-bold text-slate-800">+74 %</p>
          <p className="text-sm text-slate-600">Croissance revenus (digital)</p>
        </div>
        <div className="bg-slate-100 rounded-xl p-5 text-center">
          <Zap className="w-10 h-10 mx-auto mb-2 text-slate-600" />
          <p className="text-2xl font-bold text-slate-800">IA & data</p>
          <p className="text-sm text-slate-600">Stratégie fêtes / campagnes</p>
        </div>
      </div>

      <section className="bg-slate-50 border border-slate-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          Objectif de cette carte : former l'équipe vente et marketing au marketing web complet
        </h3>
        <p className="text-slate-700 mb-4">
          Cette carte sert à <strong>former notre équipe vente et marketing</strong> sur le marketing web dans sa totalité : <strong>site web</strong> (UX, conversion, contenu, technique), <strong>IA</strong> (personnalisation, campagnes, génération de contenu), <strong>vidéo virale</strong> (formats courts, hooks, plateformes) et <strong>contenu réseaux sociaux</strong> (LinkedIn, calendrier éditorial, métriques). Aritzia est notre exemple de référence : une entreprise qui a aligné tout ça avec des résultats business mesurables.
        </p>
        <h4 className="text-sm font-bold text-slate-700 mb-2 mt-4">Pourquoi Aritzia comme référence ?</h4>
        <p className="text-slate-700 mb-4">
          Aritzia n'est pas qu'une marque de mode : c'est un exemple concret qui a aligné <strong>site web</strong>, <strong>stratégie digitale</strong> (incl. IA et contenu) et <strong>résultats business</strong>. Leur croissance (revenus, part du digital, campagnes IA) prouve que « bien faire son marketing web » n'est pas du décor — c'est un levier de revenus et de confiance.
        </p>
        <ul className="space-y-2 text-slate-700">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <span><strong>Site orienté conversion</strong> — Chaque page a un rôle (découverte → considération → achat). UX pensée pour réduire l'abandon et augmenter le panier moyen.</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <span><strong>Stratégie digitale structurée</strong> — Omni‑canal (boutique + web + app), SEO, expérience cohérente, BI pour mesurer et optimiser.</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <span><strong>Campagnes et storytelling</strong> — Collections saisonnières, contenu qui crée de l'envie et donne des raisons d'acheter maintenant.</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <span><strong>IA et données</strong> — Personnalisation des campagnes et recommandations ; résultats mesurables (ex. records des ventes en période des fêtes).</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <span><strong>Contenu et visibilité</strong> — Visuels forts, cohérence sur les canaux ; on s'en inspire pour notre propre contenu (réseaux, vidéo, site).</span>
          </li>
        </ul>
      </section>

      <section className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-blue-900 mb-3">Comment utiliser cette documentation (et LEX)</h3>
        <p className="text-blue-900 text-sm mb-3">
          Ce contenu est pensé comme <strong>stock de référence</strong> : plus il y a de « viande » (définitions, pourquoi, exemples, pièges), plus LEX et l'équipe peuvent s'en servir pour générer des idées, des réponses et des bonnes pratiques. Utilisez les onglets pour approfondir chaque thème (UX, conversion, contenu, technique) et la section « Leçons pour BZ » pour les actions concrètes.
        </p>
        <p className="text-blue-800 text-sm italic">
          Exemples de questions à poser à LEX après lecture : « Comment appliquer le principe de hiérarchie claire à notre page PRIsME ? », « Quels CTA proposer pour une landing ODS ? », « Checklist UX pour une nouvelle page produit BZ ».
        </p>
      </section>
    </motion.div>
  );
}

// ========== UX & Navigation (enrichie) ==========
function UXSection() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <section>
        <h3 className="text-xl font-bold text-slate-800 mb-2 flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          Qu'est-ce que l'UX (User Experience) ?
        </h3>
        <p className="text-slate-700 mb-4">
          L'<strong>expérience utilisateur</strong> couvre tout ce que vit une personne qui utilise un site ou une application : facilité à trouver l'info, clarté des actions, fluidité, sentiment de confiance. Une bonne UX réduit la friction, les abandons et les erreurs ; une mauvaise UX fait fuir même si l'offre est bonne. <strong>Pourquoi c'est business :</strong> chaque seconde perdue ou clic inutile augmente le taux d'abandon. En e‑commerce, une navigation confuse peut faire chuter les conversions de 20–40 %. Pour les services (comme BZ), une interface claire renforce la crédibilité et accélère la prise de décision du client.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-bold text-slate-800 mb-3">Ce qu'Aritzia fait bien (et pourquoi)</h3>
        <ul className="space-y-4 text-slate-700">
          <li>
            <strong>Hiérarchie claire</strong> — Catégories (femme, collections, marques) accessibles en peu de clics. <em>Pourquoi :</em> le visiteur sait où aller pour trouver ce qu'il cherche ; moins de « je ne trouve pas » = moins d'abandon.
          </li>
          <li>
            <strong>Recherche et filtres (facettes)</strong> — Taille, couleur, prix, marque permettent de réduire le choix et d'accélérer la décision. <em>Pourquoi :</em> trop de choix sans filtrage crée la paralysie ; des filtres bien pensés augmentent la satisfaction et la probabilité d'achat.
          </li>
          <li>
            <strong>Mobile-first</strong> — Navigation adaptée au tactile, boutons et zones cliquables suffisamment grandes, contenu qui s'adapte. <em>Pourquoi :</em> une grande part du trafic et des achats est sur mobile ; ignorer le mobile = perdre des clients.
          </li>
          <li>
            <strong>Cohérence visuelle</strong> — Palette, typo et composants uniformes sur tout le site. <em>Pourquoi :</em> la cohérence renforce la marque et la confiance ; l'incohérence donne une impression de bricolage et fait douter de la crédibilité.
          </li>
        </ul>
      </section>

      <section className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <h3 className="text-lg font-bold text-amber-900 mb-2 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Erreurs UX à éviter
        </h3>
        <ul className="space-y-2 text-amber-900 text-sm">
          <li>• Trop de niveaux de navigation (plus de 3 clics pour atteindre l'essentiel).</li>
          <li>• Boutons ou liens trop petits sur mobile (zones de clic &lt; 44px).</li>
          <li>• Pages surchargées sans hiérarchie (tout a l'air aussi important).</li>
          <li>• Incohérence entre les pages (couleurs, libellés, placement des CTA qui changent sans logique).</li>
          <li>• Absence de filtre ou de recherche quand il y a beaucoup de contenu ou de produits.</li>
        </ul>
      </section>

      <section className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        <h3 className="text-lg font-bold text-slate-800 mb-3">Application BZ — Checklist UX</h3>
        <ul className="space-y-2 text-slate-700 text-sm">
          <li>• <strong>Structure de l'information</strong> : offres (PDTI, cybersécurité, PRIsME, ODS) accessibles en 1–2 clics depuis la page d'accueil ou le menu.</li>
          <li>• <strong>Recherche / filtres</strong> : dans PRIsME ou les portails clients, permettre de filtrer par projet, client, type de livrable, date.</li>
          <li>• <strong>Cohérence</strong> : mêmes conventions (boutons, couleurs, libellés) sur PRIsME, site BZ, propositions et présentations.</li>
          <li>• <strong>Mobile</strong> : vérifier que les pages clés (landing, contact, démo) sont utilisables sur téléphone (boutons, formulaires, lisibilité).</li>
        </ul>
      </section>

      <section className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
        <strong>Idées pour LEX :</strong> « Explique-moi la règle des 3 clics » ; « Donne-moi une checklist UX pour une nouvelle page produit BZ » ; « Comment structurer le menu de notre site pour nos 4 offres principales ? » ; « Quels filtres proposer dans notre outil de suivi de propositions ? ».
      </section>
    </motion.div>
  );
}

// ========== Conversion & CTA (enrichie) ==========
function ConversionSection() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <section>
        <h3 className="text-xl font-bold text-slate-800 mb-2 flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          Conversion et CTA : définitions et enjeux business
        </h3>
        <p className="text-slate-700 mb-3">
          La <strong>conversion</strong>, c'est le moment où un visiteur fait l'action qu'on souhaite : acheter, s'inscrire, télécharger un document, demander un rendez-vous, remplir un formulaire. Le <strong>taux de conversion</strong> = (nombre de conversions / nombre de visiteurs) × 100. Ex. : 1000 visiteurs, 30 demandes de démo → 3 % de conversion.
        </p>
        <p className="text-slate-700 mb-3">
          Le <strong>CTA (Call To Action)</strong> est l'élément (bouton, lien, phrase) qui invite explicitement à faire cette action : « Ajouter au panier », « Demander une démo », « Télécharger le guide ». <strong>Pourquoi c'est critique :</strong> sans CTA clair, le visiteur ne sait pas quoi faire ensuite ; avec un CTA visible et pertinent, on guide la décision et on augmente les conversions. Plusieurs études montrent qu'un seul CTA principal par écran performe souvent mieux que plusieurs boutons en concurrence.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-bold text-slate-800 mb-3">Ce qu'Aritzia fait bien</h3>
        <ul className="space-y-3 text-slate-700">
          <li><strong>CTA visibles et répétés</strong> — « Ajouter au panier », « Voir la collection », « S'inscrire » bien placés (above the fold, en bas de fiche produit) et répétés au bon moment. <em>Pourquoi :</em> le visiteur n'a pas à chercher ; l'action proposée est évidente.</li>
          <li><strong>Réduction de la friction</strong> — Processus de commande en étapes claires, choix de livraison et de paiement simples, pas de formulaire inutile. <em>Pourquoi :</em> chaque champ en plus augmente l'abandon ; moins de friction = plus de complétions.</li>
          <li><strong>Urgence et rareté (utilisées avec mesure)</strong> — « Plus que X en stock », soldes limitées dans le temps. <em>Pourquoi :</em> donnent une raison d'agir maintenant ; à utiliser de façon honnête pour ne pas nuire à la confiance.</li>
          <li><strong>Confiance</strong> — Avis, retours, garanties, sécurisation du paiement mis en avant. <em>Pourquoi :</em> le visiteur hésite souvent par peur de se tromper ; les preuves sociales et les garanties lèvent les objections.</li>
        </ul>
      </section>

      <section className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <h3 className="text-lg font-bold text-amber-900 mb-2 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Erreurs conversion / CTA à éviter
        </h3>
        <ul className="space-y-2 text-amber-900 text-sm">
          <li>• Plusieurs CTA en concurrence sur la même zone (« Télécharger » vs « Contacter » vs « Voir la démo » sans hiérarchie).</li>
          <li>• Formulaires trop longs (demander l'adresse complète alors qu'on a juste besoin de l'email pour une première prise de contact).</li>
          <li>• Bouton CTA noyé dans le texte ou de la même couleur que le fond.</li>
          <li>• Texte du CTA vague (« Soumettre », « Envoyer » au lieu de « Demander ma démo » ou « Télécharger le guide »).</li>
          <li>• Pas de rappel du CTA en bas de page longue (le visiteur scroll et oublie l'action).</li>
        </ul>
      </section>

      <section className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        <h3 className="text-lg font-bold text-slate-800 mb-3">Application BZ — CTA par type de page</h3>
        <ul className="space-y-2 text-slate-700 text-sm">
          <li>• <strong>Landing ODS / démo</strong> : un CTA principal du type « Demander une démo » ou « Obtenir mon ODS » ; secondaire : « Télécharger la fiche » ou « Voir le cadre BZ ».</li>
          <li>• <strong>Page offre (PDTI, cybersécurité, PRIsME)</strong> : CTA « Demander une proposition » ou « Planifier un appel » ; garder le même libellé sur tout le site pour la cohérence.</li>
          <li>• <strong>PRIsME (outil interne)</strong> : par écran, une action principale (ex. « Créer un projet », « Envoyer la proposition ») mise en évidence.</li>
          <li>• <strong>Email / signature</strong> : lien ou bouton clair vers la page de prise de rendez-vous ou la landing du moment.</li>
        </ul>
      </section>

      <section className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
        <strong>Idées pour LEX :</strong> « Propose 5 variantes de CTA pour une landing ODS » ; « Comment réduire la friction sur notre formulaire de contact ? » ; « Rédige 3 textes de CTA pour une page PDTI » ; « Où placer le CTA sur une page de proposition commerciale ? ».
      </section>
    </motion.div>
  );
}

// ========== Contenu & Storytelling (enrichie) ==========
function ContenuSection() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <section>
        <h3 className="text-xl font-bold text-slate-800 mb-2 flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          Contenu et storytelling : pourquoi ça compte
        </h3>
        <p className="text-slate-700 mb-3">
          Le <strong>contenu</strong> (textes, images, vidéos) ne sert pas qu'à « remplir » le site : il informe, rassure, donne envie et aide le moteur de recherche à comprendre la page (SEO). Le <strong>storytelling</strong> consiste à raconter une histoire ou un fil conducteur (saison, projet, bénéfice client) pour que l'offre soit mémorable et émotionnellement connectée. <strong>Pourquoi c'est business :</strong> un visiteur qui comprend vite la valeur et qui se projette (cas client, résultat) convertit mieux. Du contenu bien structuré améliore aussi le référencement (SEO) et donc le trafic organique sans payer chaque clic.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-bold text-slate-800 mb-3">Ce qu'Aritzia fait bien</h3>
        <ul className="space-y-3 text-slate-700">
          <li><strong>Collections et saisons</strong> — Récits (pré‑printemps, fêtes) qui donnent du sens aux produits et créent de l'envie. <em>Pourquoi :</em> « Nouvelle collection » ou « Soldes d'été » donne une raison d'acheter maintenant et renforce l'image de marque.</li>
          <li><strong>Visuel fort</strong> — Images de qualité, cohérence du style photo et vidéo. <em>Pourquoi :</em> le visuel est la première impression ; un visuel professionnel = crédibilité et désir.</li>
          <li><strong>SEO et mots-clés</strong> — Catégories, fiches produits et éditorial pensés pour les moteurs de recherche (titres, descriptions, URLs). <em>Pourquoi :</em> le trafic organique coûte moins cher et apporte des visiteurs souvent plus qualifiés.</li>
          <li><strong>Personnalisation</strong> — Recommandations, « Vu récemment », « Complétez votre look ». <em>Pourquoi :</em> augmenter le panier moyen et la satisfaction en montrant qu'on connaît le visiteur.</li>
        </ul>
      </section>

      <section className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <h3 className="text-lg font-bold text-amber-900 mb-2 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Erreurs contenu / storytelling à éviter
        </h3>
        <ul className="space-y-2 text-amber-900 text-sm">
          <li>• Texte générique (« Nous sommes les meilleurs ») sans preuve ni cas concret.</li>
          <li>• Images floues, incohérentes ou stock générique non adapté à la marque.</li>
          <li>• Pages sans structure (titres H1/H2), ce qui nuit au SEO et à la lisibilité.</li>
          <li>• Tout centré sur nous (produits, processus) au lieu des bénéfices et des résultats pour le client.</li>
          <li>• Pas de mise à jour (offres obsolètes, dates ou chiffres anciens).</li>
        </ul>
      </section>

      <section className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        <h3 className="text-lg font-bold text-slate-800 mb-3">Application BZ — Contenu qui rassure et qui vend</h3>
        <ul className="space-y-2 text-slate-700 text-sm">
          <li>• <strong>Raconter nos offres</strong> : PDTI, cybersécurité, PRIsME avec cas clients (avant/après), bénéfices concrets (gain de temps, réduction de risques, alignement stratégique), pas seulement la liste des livrables.</li>
          <li>• <strong>Visuels professionnels</strong> : schémas du cadre BZ, captures PRIsME, photos d'équipe ou d'événements pour humaniser la marque.</li>
          <li>• <strong>SEO</strong> : titres et meta descriptions par page (ex. « Plan Directeur TI — BZ »), mots-clés naturels (transformation TI, cybersécurité PME, méthodologie PRIsME).</li>
          <li>• <strong>Mise à jour</strong> : vérifier régulièrement que les chiffres, les offres et les témoignages sont à jour.</li>
        </ul>
      </section>

      <section className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
        <strong>Idées pour LEX :</strong> « Rédige une intro storytelling pour notre page PDTI » ; « Propose 3 angles de cas client pour la cybersécurité » ; « Liste les mots-clés SEO pour notre page PRIsME » ; « Structure le contenu d'une fiche offre BZ (sections, ton, CTA) ».
      </section>
    </motion.div>
  );
}

// ========== Technique & Performance (enrichie) ==========
function TechniqueSection() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <section>
        <h3 className="text-xl font-bold text-slate-800 mb-2 flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          Technique et performance : pourquoi c'est du marketing
        </h3>
        <p className="text-slate-700 mb-3">
          Un site lent ou qui plante n'est pas qu'un « problème technique » : c'est un <strong>problème business</strong>. Les études (Google, etc.) montrent qu'un délai de chargement de 1 à 3 secondes augmente fortement le taux de rebond ; au-delà de 5 secondes, une majorité des utilisateurs quitte. La <strong>disponibilité</strong> (site accessible 24/7, surtout en période de pic) est tout aussi critique : une panne pendant une campagne = ventes et confiance perdues. <strong>Pourquoi on en parle en marketing :</strong> la performance et la fiabilité font partie de l'expérience ; les mesurer (vitesse, erreurs, conversions) permet d'optimiser en continu et de prioriser les correctifs.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-bold text-slate-800 mb-3">Ce qu'Aritzia fait bien</h3>
        <ul className="space-y-3 text-slate-700">
          <li><strong>Vitesse</strong> — Images optimisées, lazy loading, CDN pour une expérience fluide sur mobile et desktop. <em>Pourquoi :</em> chaque seconde gagnée améliore la satisfaction et les conversions.</li>
          <li><strong>Disponibilité</strong> — Site résilient en période de forte demande (soldes, fêtes). <em>Pourquoi :</em> les pics de trafic sont des opportunités de revenus ; une chute du site = perte directe.</li>
          <li><strong>Analytics et tests</strong> — Données pour mesurer parcours, conversion et tester des variantes (A/B). <em>Pourquoi :</em> sans données, on devine ; avec des données, on optimise de façon ciblée.</li>
          <li><strong>IA et personnalisation</strong> — Utilisation de l'IA pour campagnes et recommandations (ex. records des ventes en période des fêtes). <em>Pourquoi :</em> l'IA permet de scaler la personnalisation et d'ajuster les messages en temps réel.</li>
        </ul>
      </section>

      <section className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <h3 className="text-lg font-bold text-amber-900 mb-2 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Erreurs technique / performance à éviter
        </h3>
        <ul className="space-y-2 text-amber-900 text-sm">
          <li>• Images lourdes non compressées (ralentissent le chargement, surtout sur mobile).</li>
          <li>• Pas de suivi des métriques (visites, conversions, temps de chargement, erreurs 404).</li>
          <li>• Ignorer le mobile (temps de chargement ou mise en page dégradés sur petit écran).</li>
          <li>• Pas de plan pour les pics (campagne lancée sans vérifier que le serveur ou l'hébergement tient la charge).</li>
        </ul>
      </section>

      <section className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        <h3 className="text-lg font-bold text-slate-800 mb-3">Application BZ — Technique au service du business</h3>
        <ul className="space-y-2 text-slate-700 text-sm">
          <li>• <strong>Performance</strong> : PRIsME, site BZ et portails clients — viser un chargement &lt; 3 s sur les pages clés ; optimiser les images et les scripts.</li>
          <li>• <strong>KPIs à suivre</strong> : visites, pages vues, téléchargements (guides, ODS), demandes de contact / démo, taux de rebond, temps sur page. Définir des objectifs (ex. X demandes de démo par mois).</li>
          <li>• <strong>Amélioration continue</strong> : revue trimestrielle des métriques ; tests A/B sur les CTA ou les textes des landings si le volume le permet.</li>
        </ul>
      </section>

      <section className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
        <strong>Idées pour LEX :</strong> « Quelles métriques suivre pour notre site BZ ? » ; « Checklist performance avant lancement d'une campagne » ; « Comment expliquer à un client pourquoi la vitesse du site impacte les conversions ? » ; « Propose un plan d'optimisation technique pour PRIsME ».
      </section>
    </motion.div>
  );
}

// ========== Réseaux sociaux & Vidéo (formation marketing web complète) ==========
function ReseauxVideoSection() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <section>
        <h3 className="text-xl font-bold text-slate-800 mb-2 flex items-center gap-2">
          <Share2 className="w-5 h-5" />
          Pourquoi réseaux sociaux et vidéo font partie du marketing web « total »
        </h3>
        <p className="text-slate-700 mb-3">
          Le marketing web ne se limite pas au site : <strong>réseaux sociaux</strong> (LinkedIn, etc.) et <strong>vidéo</strong> (formats courts, viraux) sont des leviers de visibilité, d'engagement et de leads. Une équipe vente et marketing formée à ces sujets peut produire du contenu cohérent, mesurer l'impact et aligner site, réseaux et campagnes. Aritzia s'appuie sur un contenu visuel fort et des campagnes saisonnières ; on s'en inspire pour notre propre stratégie (B2B, thought leadership, démos).
        </p>
      </section>

      <section>
        <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
          <Video className="w-5 h-5" />
          Vidéo virale et formats courts
        </h3>
        <p className="text-slate-700 mb-3">
          <strong>Vidéo virale</strong> = contenu court qui se partage et génère de l'engagement (vues, likes, commentaires, partages). Plateformes clés : <strong>Reels</strong> (Instagram), <strong>TikTok</strong>, <strong>YouTube Shorts</strong>, et pour le B2B : <strong>LinkedIn vidéo</strong> (démos, témoignages, tips).
        </p>
        <ul className="space-y-2 text-slate-700 mb-4">
          <li><strong>Hook (3 premières secondes)</strong> — Capturer l'attention tout de suite : question, chiffre choc, promesse ou visuel fort. Sinon, le scroll continue.</li>
          <li><strong>Format court</strong> — 15–60 s pour Reels/TikTok/Shorts ; 1–3 min pour LinkedIn selon le sujet. Message unique par vidéo.</li>
          <li><strong>Valeur immédiate</strong> — Tip concret, « comment faire », avant/après, témoignage court. Éviter le pitch pur.</li>
          <li><strong>CTA clair</strong> — « Suis pour plus », « Lien en bio », « Demande une démo » en fin de vidéo.</li>
        </ul>
        <p className="text-slate-600 text-sm italic">
          Ce qu'Aritzia fait bien : visuels et campagnes saisonnières (vidéo, lookbooks) qui créent de l'envie. Pour BZ : démos PRIsME, témoignages clients, « 3 tips cybersécurité » ou « Pourquoi un PDTI » en format court.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
          <Share2 className="w-5 h-5" />
          Réseaux sociaux — Contenu et calendrier (B2B)
        </h3>
        <p className="text-slate-700 mb-3">
          Pour BZ (B2B), <strong>LinkedIn</strong> est le canal prioritaire : thought leadership, offres (PDTI, cybersécurité, PRIsME), actualités, témoignages. Objectifs : visibilité, crédibilité, génération de leads (demandes de contact, téléchargements).
        </p>
        <ul className="space-y-2 text-slate-700 mb-4">
          <li><strong>Types de contenu</strong> — Posts texte (tips, leçons apprises), visuels (infographies, schémas cadre BZ), vidéos courtes (démo, témoignage), articles ou liens vers le site (landing ODS, blog).</li>
          <li><strong>Calendrier éditorial</strong> — Fréquence réaliste (ex. 2–3 posts/semaine). Thèmes par jour ou par semaine (lundi : tip TI, mercredi : cas client, vendredi : offre ou événement).</li>
          <li><strong>Métriques</strong> — Portée (vues), engagement (likes, commentaires, partages), clics (lien vers site), leads (formulaires, demandes de démo). Définir 2–3 KPIs et les suivre.</li>
        </ul>
        <p className="text-slate-600 text-sm italic">
          Aligner le contenu réseaux avec le site : même message, même CTA (ex. « Demande une démo », « Télécharge le guide »). Éviter le décalage entre ce qu'on dit sur LinkedIn et ce qu'on propose sur le site.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          IA en marketing (personnalisation, campagnes, contenu)
        </h3>
        <p className="text-slate-700 mb-3">
          L'<strong>IA</strong> permet de scaler la personnalisation (recommandations, messages adaptés au profil), d'optimiser les campagnes (ciblage, créatifs) et d'aider à la génération de contenu (texte, idées, visuels). Aritzia utilise l'IA pour campagnes et recommandations ; on vise la même rigueur : utiliser l'IA pour <strong>outiller</strong> l'équipe, pas remplacer le jugement humain.
        </p>
        <ul className="space-y-2 text-slate-700 mb-4">
          <li><strong>Personnalisation</strong> — Messages ou offres adaptés au segment (PME vs grand compte, secteur). Données + règles ou modèles pour personnaliser emails, landings, bannières.</li>
          <li><strong>Campagnes</strong> — Ciblage (audiences), tests A/B sur sujets ou visuels, optimisation des enchères ou du budget. Mesurer et itérer.</li>
          <li><strong>Génération de contenu</strong> — Idées de posts, brouillons de textes, suggestions de titres ou de CTA. LEX et autres outils pour accélérer la production ; validation humaine avant publication.</li>
        </ul>
        <p className="text-slate-600 text-sm italic">
          Règle BZ : l'IA aide à produire et à optimiser ; la stratégie, le ton et les décisions finales restent humains. Documenter les bonnes pratiques (prompts, revue, métriques) pour former l'équipe.
        </p>
      </section>

      <section className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <h3 className="text-lg font-bold text-amber-900 mb-2 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Erreurs à éviter (réseaux, vidéo, IA)
        </h3>
        <ul className="space-y-2 text-amber-900 text-sm">
          <li>• Vidéo sans hook : les 3 premières secondes ne captent pas → scroll.</li>
          <li>• Contenu réseaux déconnecté du site (message ou CTA différent).</li>
          <li>• Pas de calendrier ni de métriques → on ne sait pas ce qui marche.</li>
          <li>• Publier du contenu IA sans relecture (ton, faits, conformité).</li>
          <li>• Tout faire à la main sans outil IA → goulot d'étranglement.</li>
        </ul>
      </section>

      <section className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        <h3 className="text-lg font-bold text-slate-800 mb-3">Application BZ — Actions concrètes</h3>
        <ul className="space-y-2 text-slate-700 text-sm">
          <li>• <strong>Vidéo</strong> : 1–2 formats courts par mois (démo PRIsME, tip cybersécurité, témoignage) ; hook + CTA vers site ou démo.</li>
          <li>• <strong>LinkedIn</strong> : calendrier éditorial (2–3 posts/semaine), mix tips / cas clients / offres ; lien systématique vers landings (ODS, démo).</li>
          <li>• <strong>IA</strong> : utiliser LEX et outils pour idées, brouillons, checklist ; valider en interne avant publication ; suivre les métriques pour ajuster.</li>
        </ul>
      </section>

      <section className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
        <strong>Idées pour LEX :</strong> « Propose 5 hooks pour une vidéo courte sur le PDTI » ; « Rédige un calendrier éditorial LinkedIn pour le mois prochain » ; « Liste les métriques à suivre pour notre contenu réseaux » ; « Donne-moi 3 idées de posts avec IA (ton BZ, pensée PRIsME) ».
      </section>
    </motion.div>
  );
}

// ========== Leçons pour BZ (enrichie) ==========
function LeconsSection() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <section>
        <h3 className="text-xl font-bold text-slate-800 mb-3">Résumé actionnable — Ce qu'on retient d'Aritzia</h3>
        <p className="text-slate-700 mb-4">
          Un site « pensé marketing » combine <strong>UX</strong>, <strong>conversion</strong>, <strong>contenu</strong> et <strong>technique</strong>. Chaque leçon ci‑dessous est une règle ou une action que l'équipe peut appliquer tout de suite ; la documentation des onglets précédents donne le détail et le « pourquoi » pour former et pour alimenter LEX.
        </p>
      </section>

      <div className="grid gap-5">
        {[
          {
            num: 1,
            title: 'Penser « parcours utilisateur »',
            body: 'Chaque écran ou page doit avoir un but clair et mener naturellement à l\'action suivante. Ex. : Accueil → Découverte des offres → Fiche PDTI/Cyber/PRIsME → CTA « Demander une démo » ou « Obtenir une ODS ». Éviter les pages sans suite (dead end) ou sans CTA.',
            lex: 'Questions pour LEX : « Quel parcours proposer pour un visiteur qui découvre BZ ? » ; « Où placer la page cadre BZ dans le parcours ? »',
          },
          {
            num: 2,
            title: 'Un CTA principal par contexte',
            body: 'Éviter la surcharge : un bouton principal visible par page ou section (ex. « Demander une ODS », « Voir le cadre BZ », « Planifier un appel »). Les actions secondaires peuvent exister mais ne doivent pas rivaliser visuellement avec le CTA principal. Texte du CTA = action claire, pas « Soumettre » ou « Envoyer ».',
            lex: 'Questions pour LEX : « Propose 5 libellés de CTA pour notre landing démo » ; « Comment hiérarchiser CTA principal vs secondaire sur la page accueil ? »',
          },
          {
            num: 3,
            title: 'Contenu qui rassure et qui vend',
            body: 'Témoignages, cas clients, livrables tangibles (cadre, méthodologie, exemples de livrables) et visuels professionnels renforcent la crédibilité. Structurer le contenu (titres, paragraphes courts, listes) pour la lisibilité et le SEO. Mettre à jour régulièrement les chiffres et les offres.',
            lex: 'Questions pour LEX : « Rédige un bloc témoignage type pour une page offre » ; « Structure le contenu d\'une fiche PDTI en sections » ; « Liste les preuves sociales à mettre sur notre site ».',
          },
          {
            num: 4,
            title: 'Mesurer et améliorer',
            body: 'Définir des métriques (visites, téléchargements, demandes de contact, taux de conversion par page) et les suivre. Sans données, on ne sait pas ce qui fonctionne. Utiliser les chiffres pour prioriser les améliorations (quelle page optimiser en premier, quel CTA tester).',
            lex: 'Questions pour LEX : « Quels KPIs définir pour notre site BZ ? » ; « Comment mesurer l\'impact d\'une campagne email ? » ; « Propose un tableau de bord simple pour le marketing ».',
          },
          {
            num: 5,
            title: 'UX et technique au service du business',
            body: 'Navigation claire (max 3 clics pour l’essentiel), cohérence visuelle, mobile utilisable, et performance (chargement < 3 s). Les « détails » technique et design ont un impact direct sur la confiance et les conversions ; les traiter comme des leviers business, pas comme du secondaire.',
            lex: 'Questions pour LEX : « Checklist UX avant mise en ligne d\'une nouvelle page » ; « Comment justifier l\'investissement en performance site auprès de la direction ? »',
          },
          {
            num: 6,
            title: 'Réseaux sociaux, vidéo virale et IA — marketing web « total »',
            body: 'Former l\'équipe au marketing web complet : site + réseaux (LinkedIn, calendrier éditorial, métriques) + vidéo (formats courts, hooks, CTA) + IA (personnalisation, campagnes, génération de contenu avec validation humaine). Aligner contenu réseaux et site ; mesurer et itérer.',
            lex: 'Questions pour LEX : « Propose 5 hooks pour une vidéo sur le PDTI » ; « Calendrier éditorial LinkedIn pour le mois » ; « Métriques à suivre pour notre contenu réseaux » ; « 3 idées de posts avec ton BZ ».',
          },
        ].map(({ num, title, body, lex }) => (
          <div key={num} className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-700 shrink-0">
              {num}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-slate-800 mb-2">{title}</p>
              <p className="text-slate-600 text-sm mb-3">{body}</p>
              <p className="text-blue-700 text-xs italic">{lex}</p>
            </div>
          </div>
        ))}
      </div>

      <section className="bg-slate-100 border border-slate-300 rounded-xl p-5">
        <h3 className="text-lg font-bold text-slate-800 mb-2">Utilisation en réunion et avec LEX</h3>
        <p className="text-slate-700 text-sm mb-2">
          Utiliser cette carte en réunion pour <strong>former l'équipe vente et marketing</strong> au marketing web complet : site (UX, CTA, contenu, technique), <strong>réseaux sociaux</strong> (LinkedIn, calendrier, métriques), <strong>vidéo virale</strong> (formats courts, hooks) et <strong>IA</strong> (personnalisation, campagnes, génération de contenu). Chaque onglet sert de référence pour les nouveaux et pour LEX. Plus le « stock » est riche, plus LEX peut générer des idées et des bonnes pratiques. Mettre à jour la carte quand on valide de nouvelles règles ou qu'on découvre de nouveaux exemples (Aritzia ou autres).
        </p>
        <p className="text-slate-600 text-sm italic">
          Aritzia nous montre qu'un marketing web pensé (site + contenu + IA + résultats) n'est pas du hasard : c'est une combinaison d'UX, conversion, contenu, technique, réseaux et données, documentée et améliorée en continu. On vise la même rigueur pour BZ.
        </p>
      </section>
    </motion.div>
  );
}
